// Page Catalogue de solutions - V4 shadcn sidebar + tabs
// Sidebar thèmes à gauche + tabs catégories (S'informer / Sensibiliser / Équiper / Organiser)

import { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Card, CardContent } from '../components/ui'
import { trackEvent } from '../lib/analytics'
import { DiagnosticLayout, useDiagnosticSteps, useDiagnosticData, useDiagnosticProgress } from '../components/layout'
import { Checkbox } from '../components/ui/checkbox'
import { Badge } from '../components/ui/badge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs'
import { BookOpen, Users, Wrench, Building2, ExternalLink, Check, Info, AlertTriangle } from 'lucide-react'
import { cn } from '../lib/utils'
import { normalizeThemeName } from '../lib/theme-utils'
import { api } from '../services/api'
import { toast } from 'sonner'

interface SelectedMetier {
  code: string
  nom: string
  ordre: number
}

interface LocationState {
  metiersSelectionnes?: SelectedMetier[]
  metierIds?: string[]
  selectedThemes?: string[]
}

interface Solution {
  id: string
  code?: string
  methode?: string
  sousMethode?: string
  sousMethodeLabel?: string
  theme: string
  metier?: string
  metierId?: string
  typeSolution: string
  libelle: string
  suggestion: string
  lien?: string
  duree?: string
  niveauDifficulte?: string
  nature?: string
  coutNiveau?: string
}

interface GroupedSolution extends Solution {
  applicableMetierIds: string[]   // métiers de l'utilisateur pour lesquels cette solution existe
  variantIds: string[]            // tous les solution IDs des variantes (pour la restauration)
}

// Les 4 catégories de solutions
const CATEGORIES = [
  { id: "S'informer", label: "S'informer", shortLabel: 'Informer', icon: BookOpen, description: 'Pour le dirigeant' },
  { id: 'Sensibiliser', label: 'Sensibiliser', shortLabel: 'Sensibiliser', icon: Users, description: 'Pour les salariés' },
  { id: 'Equiper', label: 'Équiper', shortLabel: 'Équiper', icon: Wrench, description: 'Pour les lieux de travail' },
  { id: 'Organiser', label: 'Organiser', shortLabel: 'Organiser', icon: Building2, description: "Pour l'entreprise" },
]

// Normaliser le type de solution V6 (labels longs) vers les catégories courtes
const normalizeSolutionType = (typeSolution: string): string => {
  const lower = typeSolution.toLowerCase()
  if (lower.includes("s'informer") || lower.includes("s\\'informer")) return "S'informer"
  if (lower.includes('sensibiliser')) return 'Sensibiliser'
  if (lower.includes('equiper') || lower.includes('équiper')) return 'Equiper'
  if (lower.includes('organiser')) return 'Organiser'
  return 'Organiser'
}

// Mapping des noms de thèmes (Q1/Q2 -> solutions.json)
const THEME_MAPPING: Record<string, string> = {
  'ecrasement': 'Écrasement',
  'gestion secours': 'Gestion des secours',
  'risque routier sur voie publique': 'Risque routier sur la voie publique',
  'plan d\'action prevention': 'Plan d\'action prévention',
  'gestion des urgences': 'Gestion des secours',
}

// Trouver le thème correspondant dans solutions.json
const findMatchingTheme = (selectedTheme: string, solutionThemes: string[]): string | null => {
  const normalizedSelected = normalizeThemeName(selectedTheme)

  if (THEME_MAPPING[normalizedSelected]) {
    return THEME_MAPPING[normalizedSelected]
  }

  const exactMatch = solutionThemes.find(
    t => normalizeThemeName(t) === normalizedSelected
  )
  if (exactMatch) return exactMatch

  const inclusionMatch = solutionThemes.find(
    t => normalizeThemeName(t).includes(normalizedSelected) ||
         normalizedSelected.includes(normalizeThemeName(t))
  )
  if (inclusionMatch) return inclusionMatch

  const selectedWords = normalizedSelected.split(/\s+/).slice(0, 2).join(' ')
  const wordsMatch = solutionThemes.find(
    t => normalizeThemeName(t).startsWith(selectedWords)
  )
  if (wordsMatch) return wordsMatch

  return null
}

export default function CatalogueSolutionsPage() {
  const { id: diagnosticId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  const { metierIds, metiersSelectionnes, diagnosticState, isLoadingData } = useDiagnosticData(diagnosticId)

  const locationState = location.state as LocationState | undefined
  const [selectedThemes, setSelectedThemes] = useState<string[]>(
    locationState?.selectedThemes || []
  )

  const [allSolutions, setAllSolutions] = useState<Solution[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedSolutions, setSelectedSolutions] = useState<Map<string, Set<string>>>(new Map())
  const [activeThemeIndex, setActiveThemeIndex] = useState(0)
  const [actionStatuses, setActionStatuses] = useState<Map<string, string>>(new Map())
  // Story 08 : tracker les thèmes consultés. Bloque le passage au plan d'action tant que tous ne sont pas vus.
  const [visitedThemes, setVisitedThemes] = useState<Set<string>>(new Set())

  const diagnosticProgress = useDiagnosticProgress(diagnosticId)

  const steps = useDiagnosticSteps('solutions', diagnosticId, {
    totalMetiers: metierIds.length || 1,
    currentMetierIndex: Math.max(0, metierIds.length - 1),
    q2Completed: true,
    metiersSelectionnes: metiersSelectionnes.map(m => ({ code: m.code, nom: m.nom })),
  }, diagnosticProgress)

  // Charger les solutions depuis le JSON + restaurer les sélections de la BDD
  useEffect(() => {
    async function loadData() {
      setIsLoading(true)
      try {
        const response = await fetch('/data/referentiel/solutions.json')
        if (response.ok) {
          const json = await response.json()
          setAllSolutions(json.data || [])
        } else {
          console.warn('Solutions JSON not found')
          setAllSolutions([])
        }

        // Toujours restaurer depuis la BDD
        if (diagnosticId) {
          const diagnostic = await api.getDiagnostic(diagnosticId)
          // Restaurer les thèmes si pas de location.state
          if (selectedThemes.length === 0) {
            const savedThemes = diagnostic?.selectedThemes as string[] | null
            if (savedThemes && savedThemes.length > 0) {
              setSelectedThemes(savedThemes)
            }
          }
          // Toujours restaurer les solutions (indépendamment des thèmes)
          // Note: les IDs sauvés en BDD peuvent être des variants (pas forcément le canonique)
          // Le mapping variant→canonical sera appliqué via un effet séparé après le chargement des solutions
          const savedSolutions = diagnostic?.selectedSolutions as Array<{ solutionId: string; metierIds: string[] }> | null
          if (savedSolutions && savedSolutions.length > 0) {
            const restoredMap = new Map<string, Set<string>>()
            savedSolutions.forEach(sel => {
              restoredMap.set(sel.solutionId, new Set(sel.metierIds))
            })
            setSelectedSolutions(restoredMap)
          }
        }

        // Charger les statuts des actions existantes (pour afficher Terminé/En cours)
        if (diagnosticId) {
          try {
            const existingActions = await api.getActionPlans(diagnosticId) as Array<{ solutionId: string; statut: string }>
            const statusMap = new Map<string, string>()
            for (const action of existingActions) {
              const current = statusMap.get(action.solutionId)
              if (!current || action.statut === 'termine' || (action.statut === 'en_cours' && current === 'a_faire')) {
                statusMap.set(action.solutionId, action.statut)
              }
            }
            setActionStatuses(statusMap)
          } catch {
            // Silently fail — statuts sont optionnels
          }
        }
      } catch (err) {
        console.error('Erreur chargement solutions:', err)
        setAllSolutions([])
      } finally {
        setIsLoading(false)
      }
    }

    loadData().finally(() => {
      // Activer l'auto-save après que React ait traité les state updates du chargement initial
      // setTimeout(0) s'exécute après le cycle de rendu React
      setTimeout(() => { dataReady.current = true }, 0)
    })
  }, [diagnosticId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-sauvegarde des solutions en BDD (debounced 1s)
  // dataReady est mis à true APRÈS le chargement initial + restauration BDD
  // pour éviter que l'auto-save ne se déclenche avec les données restaurées
  const dataReady = useRef(false)
  const saveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latestSolutions = useRef(selectedSolutions)
  const latestThemes = useRef(selectedThemes)
  latestSolutions.current = selectedSolutions
  latestThemes.current = selectedThemes

  const saveSelectionsToDb = useCallback(async (solutions: Map<string, Set<string>>, themes: string[]) => {
    if (!diagnosticId) return
    const solutionsArray = Array.from(solutions.entries()).map(([solId, metiers]) => ({
      solutionId: solId,
      metierIds: Array.from(metiers),
    }))
    try {
      await api.saveSelections(diagnosticId, {
        selectedThemes: themes,
        selectedSolutions: solutionsArray,
      })
    } catch (err) {
      console.error('Auto-save solutions erreur:', err)
    }
  }, [diagnosticId])

  useEffect(() => {
    // Ne pas sauvegarder tant que le chargement initial n'est pas terminé
    if (!dataReady.current) return
    if (isLoading) return

    if (saveTimeout.current) clearTimeout(saveTimeout.current)
    saveTimeout.current = setTimeout(() => {
      saveSelectionsToDb(selectedSolutions, selectedThemes)
    }, 1000)

    return () => {
      if (saveTimeout.current) clearTimeout(saveTimeout.current)
    }
  }, [selectedSolutions, isLoading, saveSelectionsToDb, selectedThemes])

  // Flush la sauvegarde quand l'utilisateur quitte la page
  useEffect(() => {
    return () => {
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current)
        // Sauvegarder avec les dernières valeurs (refs pour éviter closure stale)
        saveSelectionsToDb(latestSolutions.current, latestThemes.current)
      }
    }
  }, [saveSelectionsToDb])

  // Thème courant
  const currentTheme = selectedThemes[activeThemeIndex] || ''

  // Story 08 : marquer le thème actif comme consulté.
  // Le 1er thème (chargement initial) compte automatiquement comme visité.
  useEffect(() => {
    if (currentTheme) {
      setVisitedThemes(prev => {
        if (prev.has(currentTheme)) return prev
        const next = new Set(prev)
        next.add(currentTheme)
        return next
      })
    }
  }, [currentTheme])

  // Story 08 : conditions de blocage
  const remainingThemes = selectedThemes.filter(t => !visitedThemes.has(t))
  const allThemesVisited = selectedThemes.length > 0 && remainingThemes.length === 0

  // Thèmes uniques des solutions (pour le matching)
  const allSolutionThemes = useMemo(() => {
    return [...new Set(allSolutions.map(s => s.theme).filter(Boolean))]
  }, [allSolutions])

  // Solutions pour le thème courant (dédupliquées par contenu)
  const solutionsForTheme = useMemo((): GroupedSolution[] => {
    if (!currentTheme || allSolutions.length === 0) return []

    const matchedTheme = findMatchingTheme(currentTheme, allSolutionThemes)
    if (!matchedTheme) return []

    const filtered = allSolutions.filter((sol) => {
      if (sol.theme !== matchedTheme) return false
      if (sol.metierId && sol.metierId !== 'ALL') {
        const metierMatch = metierIds.some((id) =>
          id.toLowerCase() === sol.metierId!.toLowerCase()
        )
        if (!metierMatch) return false
      }
      return true
    })

    // Dédupliquer : grouper par contenu identique (libelle + type + thème)
    const groups = new Map<string, GroupedSolution>()
    filtered.forEach(sol => {
      const key = `${sol.libelle}|${normalizeSolutionType(sol.typeSolution || '')}|${sol.theme}`
      const existing = groups.get(key)
      if (existing) {
        if (sol.metierId && sol.metierId !== 'ALL') {
          existing.applicableMetierIds.push(sol.metierId)
        }
        existing.variantIds.push(sol.id)
      } else {
        groups.set(key, {
          ...sol,
          applicableMetierIds: sol.metierId && sol.metierId !== 'ALL'
            ? [sol.metierId]
            : [...metierIds],
          variantIds: [sol.id],
        })
      }
    })

    return Array.from(groups.values())
  }, [allSolutions, allSolutionThemes, currentTheme, metierIds])

  // Index global variant→canonical pour toutes les solutions (pas juste le thème actif)
  // Nécessaire pour la restauration depuis la BDD et le compteur par thème
  const allGroupedSolutions = useMemo((): GroupedSolution[] => {
    if (allSolutions.length === 0 || metierIds.length === 0) return []

    const filtered = allSolutions.filter((sol) => {
      if (sol.metierId && sol.metierId !== 'ALL') {
        return metierIds.some((id) => id.toLowerCase() === sol.metierId!.toLowerCase())
      }
      return true
    })

    const groups = new Map<string, GroupedSolution>()
    filtered.forEach(sol => {
      const key = `${sol.libelle}|${normalizeSolutionType(sol.typeSolution || '')}|${sol.theme}`
      const existing = groups.get(key)
      if (existing) {
        if (sol.metierId && sol.metierId !== 'ALL') {
          existing.applicableMetierIds.push(sol.metierId)
        }
        existing.variantIds.push(sol.id)
      } else {
        groups.set(key, {
          ...sol,
          applicableMetierIds: sol.metierId && sol.metierId !== 'ALL'
            ? [sol.metierId]
            : [...metierIds],
          variantIds: [sol.id],
        })
      }
    })

    return Array.from(groups.values())
  }, [allSolutions, metierIds])

  // Mapping variant ID → canonical ID (premier du groupe)
  const variantToCanonical = useMemo(() => {
    const map = new Map<string, string>()
    allGroupedSolutions.forEach(group => {
      group.variantIds.forEach(vid => map.set(vid, group.id))
    })
    return map
  }, [allGroupedSolutions])

  // Remapper les IDs variants vers les IDs canoniques après le chargement
  const hasRemapped = useRef(false)
  useEffect(() => {
    if (hasRemapped.current) return
    if (variantToCanonical.size === 0) return
    if (selectedSolutions.size === 0) return

    // Vérifier s'il y a des IDs à remapper
    let needsRemap = false
    for (const solId of selectedSolutions.keys()) {
      const canonical = variantToCanonical.get(solId)
      if (canonical && canonical !== solId) {
        needsRemap = true
        break
      }
    }

    if (!needsRemap) {
      hasRemapped.current = true
      return
    }

    hasRemapped.current = true
    setSelectedSolutions(prev => {
      const remapped = new Map<string, Set<string>>()
      prev.forEach((metiers, solId) => {
        const canonical = variantToCanonical.get(solId) || solId
        const existing = remapped.get(canonical)
        if (existing) {
          // Fusionner les métiers
          metiers.forEach(m => existing.add(m))
        } else {
          remapped.set(canonical, new Set(metiers))
        }
      })
      return remapped
    })
  }, [variantToCanonical, selectedSolutions])

  // Grouper par catégorie
  const solutionsByCategory = useMemo(() => {
    const grouped: Record<string, GroupedSolution[]> = {
      "S'informer": [],
      'Sensibiliser': [],
      'Equiper': [],
      'Organiser': [],
    }

    solutionsForTheme.forEach((sol) => {
      const type = normalizeSolutionType(sol.typeSolution || 'Organiser')
      if (grouped[type]) {
        grouped[type].push(sol)
      } else {
        grouped['Organiser'].push(sol)
      }
    })

    return grouped
  }, [solutionsForTheme])

  // Catégories non vides pour le thème actif
  const activeCategories = useMemo(() => {
    return CATEGORIES.filter(cat => (solutionsByCategory[cat.id] || []).length > 0)
  }, [solutionsByCategory])

  // Toggle sélection solution (avec support GroupedSolution)
  const toggleSolution = (solutionId: string, metierId?: string, applicableMetierIds?: string[]) => {
    setSelectedSolutions((prev) => {
      const newMap = new Map(prev)
      const currentMetiers = new Set(prev.get(solutionId) || [])
      // Métiers applicables pour cette solution (ceux pour lesquels elle existe)
      const applicable = applicableMetierIds || metierIds

      if (metierId) {
        if (currentMetiers.has(metierId)) {
          currentMetiers.delete(metierId)
        } else {
          currentMetiers.add(metierId)
        }
      } else {
        if (currentMetiers.size > 0) {
          currentMetiers.clear()
        } else {
          applicable.forEach(id => currentMetiers.add(id))
        }
      }

      if (currentMetiers.size === 0) {
        newMap.delete(solutionId)
      } else {
        newMap.set(solutionId, currentMetiers)
      }

      return newMap
    })
  }

  // Vérifier si une solution est sélectionnée
  const isSolutionSelected = (solutionId: string): boolean => {
    const metiers = selectedSolutions.get(solutionId)
    return metiers ? metiers.size > 0 : false
  }

  // Compteur actions total
  const actionCount = selectedSolutions.size

  // Compter les solutions sélectionnées pour un thème donné (dédupliquées)
  const getSelectedCountForTheme = (themeIndex: number): number => {
    const theme = selectedThemes[themeIndex]
    if (!theme) return 0
    const matchedTheme = findMatchingTheme(theme, allSolutionThemes)
    if (!matchedTheme) return 0
    // Utiliser les groupes dédupliqués (pas les solutions brutes)
    const themeGroups = allGroupedSolutions.filter(s => s.theme === matchedTheme)
    return themeGroups.filter(s => isSolutionSelected(s.id)).length
  }

  // Navigation
  const handleContinue = async () => {
    trackEvent('valider solutions')
    const solutionsArray = Array.from(selectedSolutions.entries()).map(([solId, metiers]) => ({
      solutionId: solId,
      metierIds: Array.from(metiers),
    }))

    // Sauvegarder les solutions sélectionnées en BDD
    if (diagnosticId) {
      try {
        await api.saveSelections(diagnosticId, {
          selectedThemes,
          selectedSolutions: solutionsArray,
        })
      } catch (err) {
        console.error('Erreur sauvegarde sélections:', err)
        toast.error('Erreur lors de la sauvegarde des solutions')
      }
    }

    navigate(`/diagnostic/${diagnosticId}/finalisation`, {
      state: {
        ...diagnosticState,
        selectedThemes,
        selectedSolutions: solutionsArray,
      },
    })
  }

  const handleBack = () => {
    navigate(`/diagnostic/${diagnosticId}/themes`, {
      state: diagnosticState,
    })
  }

  // Chargement
  if (isLoading || isLoadingData) {
    return (
      <DiagnosticLayout
        steps={steps}
        showNavigation={false}
        diagnosticState={diagnosticState}
      >
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-muted-foreground">Chargement des solutions...</p>
          </div>
        </div>
      </DiagnosticLayout>
    )
  }

  return (
    <DiagnosticLayout
      steps={steps}
      onBack={handleBack}
      onNext={handleContinue}
      backLabel="Choix des thèmes"
      nextLabel={`Finaliser (${actionCount} action${actionCount > 1 ? 's' : ''})`}
      nextDisabled={!allThemesVisited}
      diagnosticState={diagnosticState}
    >
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Catalogue de solutions</h1>
        {/* US-26 : texte d'aide rendu plus visible (taille + gris foncé) */}
        <p className="text-base text-foreground/80 mt-1.5">
          Sélectionner les 6 actions maximum à mettre en œuvre pour les thèmes sélectionnés.
        </p>
      </div>

      {/* Story 08 : bulle d'info tant que tous les thèmes ne sont pas consultés */}
      {!allThemesVisited && selectedThemes.length > 0 && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
          <Info className="size-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900">
              {remainingThemes.length} thème{remainingThemes.length > 1 ? 's' : ''} restant{remainingThemes.length > 1 ? 's' : ''} à consulter
            </p>
            <p className="text-sm text-amber-800 mt-0.5">
              Parcourez l'ensemble des thèmes (sidebar à gauche) avant de finaliser votre plan d'actions. Le bouton « Finaliser » se débloquera une fois tous les thèmes consultés.
            </p>
          </div>
        </div>
      )}

      {/* US-26 : compteur d'actions + retour visuel quand on dépasse la limite de 6 */}
      <div className="mb-6 flex items-center gap-2">
        <p className="text-base">
          <span className={cn(
            'font-semibold',
            actionCount > 6
              ? 'text-destructive'
              : actionCount > 0
                ? 'text-primary'
                : 'text-muted-foreground'
          )}>
            {actionCount} action{actionCount > 1 ? 's' : ''} sélectionnée{actionCount > 1 ? 's' : ''}
          </span>
          <span className={cn(
            'ml-1',
            actionCount > 6 ? 'text-destructive' : 'text-muted-foreground'
          )}>
            /6
          </span>
        </p>
        {actionCount > 6 && (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-destructive/10 text-destructive text-xs font-medium">
            <AlertTriangle className="size-3.5" />
            Limite de 6 actions dépassée
          </span>
        )}
      </div>

      {/* Layout principal : sidebar thèmes + contenu */}
      <Card className="overflow-hidden p-0 gap-0">
        <CardContent className="p-0">
          <div className="flex flex-col lg:flex-row">

            {/* ── Sidebar thèmes (desktop) ── */}
            <div className="hidden lg:flex lg:flex-col w-56 flex-shrink-0 border-r border-border bg-gradient-to-b from-primary/5 to-muted/30 p-3 space-y-1">
              <p className="text-xs font-semibold text-primary uppercase tracking-wider px-2 py-2">
                Thèmes ({selectedThemes.length})
              </p>
              {selectedThemes.map((theme, index) => {
                const isActive = index === activeThemeIndex
                const selectedForTheme = getSelectedCountForTheme(index)

                return (
                  <button
                    key={theme}
                    onClick={() => setActiveThemeIndex(index)}
                    className={cn(
                      'w-full text-left px-3 py-2.5 rounded-lg transition-colors text-sm',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-muted text-foreground'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={isActive ? 'secondary' : 'outline'}
                        className={cn(
                          'w-5 h-5 p-0 rounded-full text-[10px] font-bold flex items-center justify-center flex-shrink-0',
                          isActive && 'bg-primary-foreground text-primary',
                          !isActive && selectedForTheme > 0 && 'bg-primary text-primary-foreground border-primary'
                        )}
                      >
                        {selectedForTheme > 0 && !isActive ? (
                          <Check className="size-3" />
                        ) : (
                          index + 1
                        )}
                      </Badge>
                      <span className="truncate font-medium">{theme}</span>
                    </div>

                    {selectedForTheme > 0 && (
                      <div className={cn(
                        'mt-1 ml-7 text-xs',
                        isActive ? 'text-primary-foreground/70' : 'text-muted-foreground'
                      )}>
                        {selectedForTheme} action{selectedForTheme > 1 ? 's' : ''}
                      </div>
                    )}
                  </button>
                )
              })}
            </div>

            {/* ── Mobile : tabs thèmes horizontales ── */}
            <div className="lg:hidden border-b border-border p-3 w-full">
              <div className="flex gap-1 overflow-x-auto">
                {selectedThemes.map((theme, index) => {
                  const isActive = index === activeThemeIndex
                  const selectedForTheme = getSelectedCountForTheme(index)

                  return (
                    <button
                      key={theme}
                      onClick={() => setActiveThemeIndex(index)}
                      className={cn(
                        'flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap flex-shrink-0 transition-colors',
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : selectedForTheme > 0
                            ? 'bg-primary/10 text-primary'
                            : 'bg-muted text-muted-foreground'
                      )}
                    >
                      <span className={cn(
                        'w-4 h-4 rounded-full text-[10px] font-bold inline-flex items-center justify-center',
                        isActive
                          ? 'bg-primary-foreground/20'
                          : selectedForTheme > 0
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted-foreground/20'
                      )}>
                        {selectedForTheme > 0 && !isActive ? (
                          <Check className="size-2.5" />
                        ) : (
                          index + 1
                        )}
                      </span>
                      {theme.length > 20 ? theme.substring(0, 20) + '...' : theme}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* ── Zone de contenu ── */}
            <div className="flex-1 p-5 min-w-0">

              {/* Header thème actif */}
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {activeThemeIndex + 1}
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">{currentTheme}</h2>
                    <p className="text-xs text-muted-foreground">
                      {solutionsForTheme.length} solution{solutionsForTheme.length > 1 ? 's' : ''} disponible{solutionsForTheme.length > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <Badge variant="outline" className="text-xs text-muted-foreground flex-shrink-0">
                  {activeThemeIndex + 1} / {selectedThemes.length}
                </Badge>
              </div>

              {/* Pas de solutions */}
              {solutionsForTheme.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-muted-foreground font-medium">Aucune solution trouvée pour ce thème</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Passez au thème suivant ou contactez l'OPPBTP pour plus d'informations
                  </p>
                </div>
              ) : (
                /* Tabs catégories */
                <Tabs defaultValue="all" key={activeThemeIndex}>
                  <TabsList className="mb-4">
                    <TabsTrigger value="all" className="text-xs">
                      Toutes ({solutionsForTheme.length})
                    </TabsTrigger>
                    {activeCategories.map((cat) => {
                      const count = (solutionsByCategory[cat.id] || []).length
                      return (
                        <TabsTrigger key={cat.id} value={cat.id} className="text-xs gap-1">
                          <cat.icon className="size-3.5" />
                          {cat.shortLabel}
                          <span className="text-muted-foreground ml-0.5">({count})</span>
                        </TabsTrigger>
                      )
                    })}
                  </TabsList>

                  {/* Tab "Toutes" — groupé par catégorie */}
                  <TabsContent value="all" className="mt-0">
                    <div className="space-y-6">
                      {activeCategories.map((cat) => {
                        const solutions = solutionsByCategory[cat.id] || []
                        return (
                          <div key={cat.id}>
                            <div className="flex items-center gap-2.5 mb-3 py-2 px-3 bg-primary/5 rounded-lg">
                              <div className="w-6 h-6 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                                <cat.icon className="size-3.5 text-primary" />
                              </div>
                              <h3 className="text-sm font-semibold text-foreground">{cat.label}</h3>
                              <span className="text-xs text-muted-foreground">{cat.description}</span>
                            </div>
                            <SolutionsList
                              solutions={solutions}
                              isSolutionSelected={isSolutionSelected}
                              selectedSolutions={selectedSolutions}
                              toggleSolution={toggleSolution}
                              metierIds={metierIds}
                              metiersSelectionnes={metiersSelectionnes}
                              actionStatuses={actionStatuses}
                            />
                          </div>
                        )
                      })}
                    </div>
                  </TabsContent>

                  {/* Tabs par catégorie */}
                  {activeCategories.map((cat) => (
                    <TabsContent key={cat.id} value={cat.id} className="mt-0">
                      <SolutionsList
                        solutions={solutionsByCategory[cat.id] || []}
                        isSolutionSelected={isSolutionSelected}
                        selectedSolutions={selectedSolutions}
                        toggleSolution={toggleSolution}
                        metierIds={metierIds}
                        metiersSelectionnes={metiersSelectionnes}
                        actionStatuses={actionStatuses}
                      />
                    </TabsContent>
                  ))}
                </Tabs>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </DiagnosticLayout>
  )
}

// ── Composant liste de solutions ──

function SolutionsList({
  solutions,
  isSolutionSelected,
  selectedSolutions,
  toggleSolution,
  metierIds,
  metiersSelectionnes,
  actionStatuses,
}: {
  solutions: GroupedSolution[]
  isSolutionSelected: (id: string) => boolean
  selectedSolutions: Map<string, Set<string>>
  toggleSolution: (id: string, metierId?: string, applicableMetierIds?: string[]) => void
  metierIds: string[]
  metiersSelectionnes: { code: string; nom: string }[]
  actionStatuses: Map<string, string>
}) {
  if (solutions.length === 0) {
    return (
      <div className="text-center py-8 text-sm text-muted-foreground">
        Aucune solution disponible pour cette catégorie.
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {solutions.map((sol) => {
        const isSelected = isSolutionSelected(sol.id)
        const selectedMetiers = selectedSolutions.get(sol.id) || new Set()
        // Filtrer metiersSelectionnes aux seuls métiers applicables
        const applicableMetiers = metiersSelectionnes.filter(m =>
          sol.applicableMetierIds.some(id => id.toLowerCase() === m.code.toLowerCase())
        )
        const showMetierBadges = metierIds.length > 1 && applicableMetiers.length > 0

        return (
          <div
            key={sol.id}
            onClick={() => toggleSolution(sol.id, undefined, sol.applicableMetierIds)}
            className={cn(
              'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
              isSelected
                ? 'border-primary bg-primary/5'
                : 'border-border hover:bg-muted/50'
            )}
          >
            {/* Checkbox */}
            <Checkbox
              checked={isSelected}
              className="flex-shrink-0 mt-0.5 pointer-events-none"
            />

            {/* Contenu */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className={cn(
                    'text-sm',
                    isSelected && 'font-medium text-foreground'
                  )}>
                    {sol.libelle || sol.suggestion}
                  </p>
                  {actionStatuses.get(sol.id) === 'termine' && (
                    <Badge className="text-[11px] px-2 py-0.5 bg-green-100 text-green-700 border-0 gap-1 shrink-0">
                      <Check className="size-3" /> Terminé
                    </Badge>
                  )}
                  {actionStatuses.get(sol.id) === 'en_cours' && (
                    <Badge className="text-[11px] px-2 py-0.5 bg-blue-100 text-blue-700 border-0 shrink-0">
                      En cours
                    </Badge>
                  )}
                  {actionStatuses.get(sol.id) === 'a_faire' && (
                    <Badge className="text-[11px] px-2 py-0.5 bg-gray-100 text-gray-600 border-0 shrink-0">
                      Planifié
                    </Badge>
                  )}
                </div>

                {/* Lien externe */}
                {sol.lien && (
                  <a
                    href={sol.lien}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium text-primary bg-primary/10 hover:bg-primary/20 transition-colors flex-shrink-0"
                  >
                    Voir
                    <ExternalLink className="size-3" />
                  </a>
                )}
              </div>

              {/* Métadonnées */}
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                {sol.nature && (
                  <Badge className="text-[10px] px-1.5 py-0 bg-primary/15 text-primary border-0 font-medium">
                    {sol.nature}
                  </Badge>
                )}
                {sol.duree && (
                  <Badge className="text-[10px] px-1.5 py-0 bg-primary/10 text-primary/80 border-0">
                    {sol.duree}
                  </Badge>
                )}
                {sol.niveauDifficulte && (
                  <Badge className="text-[10px] px-1.5 py-0 bg-primary/10 text-primary/80 border-0">
                    {sol.niveauDifficulte}
                  </Badge>
                )}
                {sol.coutNiveau && sol.coutNiveau !== 'Variable' && (
                  <Badge className="text-[10px] px-1.5 py-0 bg-primary/10 text-primary/80 border-0">
                    {sol.coutNiveau}
                  </Badge>
                )}
              </div>

              {/* Métiers applicables — toujours visibles si >1 métier */}
              {showMetierBadges && (
                <div className="mt-2.5 pt-2.5 border-t border-border">
                  <div className="text-xs text-muted-foreground mb-1.5">
                    {isSelected ? 'Métiers concernés :' : `${applicableMetiers.length} métier${applicableMetiers.length > 1 ? 's' : ''}`}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {applicableMetiers.map((metier) => {
                      const isMetierSelected = selectedMetiers.has(metier.code)
                      return (
                        <button
                          key={metier.code}
                          onClick={(e) => {
                            e.stopPropagation()
                            if (isSelected) {
                              toggleSolution(sol.id, metier.code, sol.applicableMetierIds)
                            }
                          }}
                          className={cn(
                            'px-2 py-1 rounded-lg text-xs font-medium transition-colors border',
                            isSelected
                              ? isMetierSelected
                                ? 'bg-primary/10 text-primary border-primary/30'
                                : 'bg-muted text-muted-foreground border-transparent hover:bg-muted/80'
                              : 'bg-muted/50 text-muted-foreground border-transparent cursor-default'
                          )}
                        >
                          {metier.nom || metier.code}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
